import { z } from 'zod';

import type { LabReview, LabReviewPayment } from '../model/types';

const instant = z.string().datetime({ offset: true });

/** Validated at the boundary, mapped straight to the entity type (§7.4). */
export const labReviewSchema = z.object({
  labReviewId: z.string().min(1),
  patientId: z.string().min(1),
  patientLabel: z.string().nullable(),
  patientIdentifier: z.string().nullable().default(null),
  status: z.enum(['granted', 'paid', 'closed', 'revoked', 'expired']),
  source: z.enum(['visit', 'consultation', 'manual']),
  grantedAt: instant,
  expiresAt: instant,
  fee: z.object({ minorAmount: z.number().int().nonnegative(), currency: z.string() }),
  paidAt: instant.nullable(),
  endedAt: instant.nullable(),
  practitionerName: z.string(),
  messageCount: z.number().int().nonnegative(),
  awaitsClinicReply: z.boolean(),
  hasUnseenReply: z.boolean(),
  isOpenForPatient: z.boolean(),
  awaitingInvitation: z.boolean(),
});

export const labReviewListSchema = z.object({ items: z.array(labReviewSchema) });

export const labReviewPaymentSchema = z.object({
  paymentUrl: z.string().url().nullable(),
  creditedMinorAmount: z.number().int().nonnegative(),
  isPaid: z.boolean(),
});

export function toLabReview(dto: z.infer<typeof labReviewSchema>): LabReview {
  return {
    id: dto.labReviewId,
    patientId: dto.patientId,
    patientLabel: dto.patientLabel,
    patientIdentifier: dto.patientIdentifier,
    status: dto.status,
    source: dto.source,
    grantedAt: new Date(dto.grantedAt),
    expiresAt: new Date(dto.expiresAt),
    feeMinor: dto.fee.minorAmount,
    paidAt: dto.paidAt === null ? null : new Date(dto.paidAt),
    endedAt: dto.endedAt === null ? null : new Date(dto.endedAt),
    practitionerName: dto.practitionerName,
    messageCount: dto.messageCount,
    awaitsClinicReply: dto.awaitsClinicReply,
    hasUnseenReply: dto.hasUnseenReply,
    isOpenForPatient: dto.isOpenForPatient,
    awaitingInvitation: dto.awaitingInvitation,
  };
}

export function toLabReviews(dto: z.infer<typeof labReviewListSchema>): readonly LabReview[] {
  return dto.items.map(toLabReview);
}

export function toLabReviewPayment(dto: z.infer<typeof labReviewPaymentSchema>): LabReviewPayment {
  return {
    paymentUrl: dto.paymentUrl,
    creditedMinor: dto.creditedMinorAmount,
    isPaid: dto.isPaid,
  };
}
